import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, CircularProgress } from '@mui/material';
import PolarisIcon from '../PolarisIcon';
import { useToast } from '../Toast';
import { selectCanExportReport, selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import {
  selectCanDownloadJobsReport,
  selectCanDownloadOrdersReport,
  selectCanDownloadTasksReport,
  selectCanDownloadPendingAssetsReport
} from '@/redux/selectors/authSelectors';
import { exportReport } from '@/redux/slices/dataTableSlice';
import { selectDraftState } from '@/redux/selectors/orderPlacementSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

interface ExportReportProps {
  disabled?: boolean;
}

const ExportReport = ({ disabled }: ExportReportProps) => {
  const dispatch = useAppDispatch();
  const canExport = useAppSelector(selectCanExportReport);
  const canDownloadTasksReport = useAppSelector(selectCanDownloadTasksReport);
  const canDownloadOrdersReport = useAppSelector(selectCanDownloadOrdersReport);
  const canDownloadJobsReport = useAppSelector(selectCanDownloadJobsReport);
  const canDownloadPendingAssetsReport = useAppSelector(selectCanDownloadPendingAssetsReport);
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const [exporting, setExporting] = useState(false);
  const { success, info, error } = useToast();
  const draftState = useAppSelector(selectDraftState);

  const hideExport = useMemo(() => {
    switch (currentTableKey) {
      case 'orders':
        return !canDownloadOrdersReport;
      case 'jobs':
        return !canDownloadJobsReport;
      case 'tasks':
      case 'processingTasks':
        return !canDownloadTasksReport;
      case 'pendingAssets':
        return !canDownloadPendingAssetsReport;
      default:
        return false;
    }
  }, [currentTableKey, canDownloadTasksReport, canDownloadOrdersReport, canDownloadJobsReport]);

  if ((!draftState.draftId && !canExport) || hideExport) {
    return null;
  }

  const onDownload = () => {
    setExporting(true);
    (dispatch(exportReport(draftState.draftId ?? undefined)) as any)
      .unwrap()
      .then((res: { message?: string }) => {
        if (res?.message) {
          info(res.message);
        } else {
          success('Your report has been downloaded!');
        }
      })
      .catch(() => error('There was a problem downloading the report.'))
      .then(() => setExporting(false));
  };

  return (
    <ExportButtonWrapper table={+!draftState.draftId}>
      <StyledButton
        data-testid="export-report"
        aria-controls="export-report"
        onClick={onDownload as any}
        color="primary"
        size="large"
        startIcon={exporting ? <CircularProgress size={20} /> : <PolarisIcon icon="download" height={18} />}
        disabled={disabled || exporting}
      >
        Export Report
      </StyledButton>
    </ExportButtonWrapper>
  );
};

export default ExportReport;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;

const ExportButtonWrapper = styled.div<{ table: number }>`
  ${({ theme, table }) =>
    table &&
    `
    margin-left: auto;
    margin-right: ${theme.spacing(3)};
    `}
`;
